import { useEffect, useRef, useState } from "react";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { useTranslation } from "../../core/translation.core";
import { FancyCheckbox } from "../reuseables/form.comp";
import { ProductType } from "../../types/product";
import ProductTypeBadge from "./productTypeBadge.comp";

export default function ProductTypeMultipleSelectorComp<T extends FieldValues>({
  methods,
  name,
}: {
  methods: UseFormReturn<T, any>;
  name: Path<T>;
}) {
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t, msg } = useTranslation();

  useEffect(() => {
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  function onClick(e: MouseEvent) {
    if (!ref || !ref.current) return;
    const isOpened = (ref.current as any).getAttribute("data-state");

    if (!isOpened) return;

    const isWithin = (e.target as any).closest("#product-type-select");

    if (!isWithin) {
      setIsOpened(false);
    }
  }

  const productTypes: { value: ProductType; label: string }[] = [
    { value: "simple", label: t(msg.product.simple) },
    { value: "variationParent", label: t(msg.product.variationParent) },
    { value: "variationChild", label: t(msg.product.variationChild) },
    { value: "custom", label: t(msg.product.custom) },
    { value: "bundle", label: t(msg.product.bundleProducts) },
    { value: "giftCard", label: t(msg.product.giftCard) },
    { value: "recycle", label: t(msg.recycle.recycle) },
  ];

  return (
    <div id="product-type-select">
      <p>{t(msg.product.productType)}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {productTypes.map((type) => (
          <FancyCheckbox
            key={type.value}
            labelId={"product-type-labels-" + type.value}
            value={type.value}
            register={methods.register(name)}
          >
            {type.label}
          </FancyCheckbox>
        ))}
      </div>
    </div>
  );
}

function Panel<T extends FieldValues>({ methods, name }: { methods: UseFormReturn<T, any>; name: Path<T> }) {
  const { t, msg } = useTranslation();

  const productTypes: { value: ProductType; label: string }[] = [
    { value: "simple", label: t(msg.product.simple) },
    { value: "variationParent", label: t(msg.product.variationParent) },
    { value: "variationChild", label: "Variation Product (Child)" },
    { value: "custom", label: t(msg.product.custom) },
    { value: "bundle", label: t(msg.product.bundleProducts) },
    { value: "giftCard", label: t(msg.product.giftCard) },
    { value: "recycle", label: t(msg.recycle.recycle) },
  ];

  return (
    <div
      id="product-type-panel"
      className="product-type-panel absolute top-[34px] left-0 w-full border rounded-b-md bg-white dark:bg-her-backblue space-y-1 px-4 py-4 max-h-72 overflow-y-auto z-10"
    >
      {productTypes.map((type) => (
        <ProductTypeRow key={type.value} productType={type} methods={methods} name={name} />
      ))}
    </div>
  );
}

function ProductTypeRow<T extends FieldValues>({
  productType,
  methods,
  name,
}: {
  productType: { value: ProductType; label: string };
  methods: UseFormReturn<T, any>;
  name: Path<T>;
}) {
  return (
    <div className="w-full text-left">
      <FancyCheckbox labelId="product-type-labels" value={productType.value} register={methods.register(name)}>
        {productType.label}
      </FancyCheckbox>
    </div>
  );
}
